import { useEffect } from 'react';
import { flushSync } from 'react-dom';
import Vec2 from 'vec2';

import { useDrawingShapeContext } from '../../drawingShapeContext';

import { DEFAULT_DEPTH } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { UserDesignOperation } from '~/core/enums';
import {
  cleanSkeletonPoints,
  generateSkeletonData,
  getOutlinePts,
} from '~/core/helpers';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import {
  selectActiveFreeDrawing,
  updateActiveFreeDrawing,
} from '~/redux/slices/fabric';

export const useFreeDrawHandler = () => {
  const dispatch = useAppDispatch();

  const activeFreeDrawing = useAppSelector(selectActiveFreeDrawing);

  const { registerUserAction } = useRegisterUserAction();

  const {
    getters: { drawingShapes },
    handlers: {
      updateDrawingShapes,
      updateActiveDrawingShapeId,
      autoPlaceShapeTranslation,
    },
  } = useDrawingShapeContext();

  // free-draw add shape
  useEffect(() => {
    if (activeFreeDrawing?.length) {
      // create the skeleton, then clean it up first to avoid future errors
      const skData = cleanSkeletonPoints(
        generateSkeletonData(activeFreeDrawing, DEFAULT_DEPTH * 0.5)
      );

      if (skData) {
        const outlineData = getOutlinePts(skData) || [];
        const newPoly = outlineData.map(
          (corner) => corner.coords || new Vec2(0, 0)
        );
        const destVector = autoPlaceShapeTranslation(newPoly);

        for (const skPoint of skData) {
          skPoint.pos.subtract(destVector);
        }
        const dShape = drawingTool.buildShapeFromSkeleton(skData, false);

        if (dShape) {
          flushSync(() => {
            updateDrawingShapes([...drawingShapes, dShape]);
          });
          updateActiveDrawingShapeId(dShape.id);
          registerUserAction(UserDesignOperation.DRAW_SHAPE);
        }

        dispatch(updateNeedRedrawCanvas(true));
      }
      // prevents free-drawn shape unwanted spawning upon return from Summary page
      dispatch(updateActiveFreeDrawing([]));
    }
  }, [
    activeFreeDrawing,
    autoPlaceShapeTranslation,
    dispatch,
    drawingShapes,
    updateActiveDrawingShapeId,
    updateDrawingShapes,
    registerUserAction,
  ]);
};
